import type { LoginAuditRow } from './login-audit-types';
import { runLoginAuditAnalysis } from './login-audit-analysis';
import { renderLoginAuditResults } from './login-audit-results';

// ── Module state ──────────────────────────────────────────────────────────

export let auditRawRows:  LoginAuditRow[] = [];
export let auditSortCol  = 'loginsInRange';
export let auditSortAsc  = false;
export let auditSearch   = '';
export let auditPage     = 0;
export let auditPageSize = 50;
export let auditUserType: 'all' | 'staff' | 'drivers' = 'all';
let auditInitialized     = false;

export function setAuditSortCol(col: string):                          void { auditSortCol  = col;  }
export function setAuditSortAsc(asc: boolean):                         void { auditSortAsc  = asc;  }
export function setAuditSearch(s: string):                             void { auditSearch   = s;    }
export function setAuditPage(p: number):                               void { auditPage     = p;    }
export function setAuditPageSize(n: number):                           void { auditPageSize = n;    }
export function setAuditUserType(t: 'all' | 'staff' | 'drivers'):     void { auditUserType = t;    }
export function setAuditRawRows(rows: LoginAuditRow[]): void {
  auditRawRows = rows;
  auditPage    = 0;   // reset to first page whenever new data arrives
}

// ── Formatters ────────────────────────────────────────────────────────────

export function fmtLoginDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

// ── Page init ─────────────────────────────────────────────────────────────

export function initLoginAuditPage(): void {
  if (!auditInitialized) {
    laSetRange('30d');
    auditInitialized = true;
  }

  const runBtn = document.getElementById('laRunBtn') as HTMLButtonElement & { _wired?: boolean };
  if (runBtn._wired) return;
  runBtn._wired = true;

  runBtn.addEventListener('click', runLoginAuditAnalysis);

  document.getElementById('laExportBtn')!.addEventListener('click', async () => {
    const { exportAuditXlsx } = await import('./login-audit-export');
    exportAuditXlsx();
  });

  // Date range quick buttons
  document.querySelectorAll<HTMLButtonElement>('.la-qr-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll<HTMLButtonElement>('.la-qr-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      laSetRange(btn.dataset.range!);
    });
  });

  // Search input — resets page to 0 on every keystroke
  const searchInput = document.getElementById('laSearch') as HTMLInputElement | null;
  searchInput?.addEventListener('input', () => {
    setAuditSearch(searchInput.value);
    setAuditPage(0);
    laRecalculate();
  });

  // Rows-per-page selector
  const pageSizeSel = document.getElementById('laPageSize') as HTMLSelectElement | null;
  pageSizeSel?.addEventListener('change', () => {
    setAuditPageSize(parseInt(pageSizeSel.value) || 9999);
    setAuditPage(0);
    laRecalculate();
  });

  // Pagination click — event delegation so dynamically-rendered buttons work
  document.getElementById('laPagination')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-page]') as HTMLElement | null;
    if (btn && !btn.hasAttribute('disabled')) {
      setAuditPage(parseInt(btn.dataset.page!));
      laRecalculate();
    }
  });

  // User type filter tabs (All / Staff / Drivers)
  document.getElementById('laTypeFilter')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-type]') as HTMLElement | null;
    if (!btn) return;
    document.querySelectorAll<HTMLButtonElement>('#laTypeFilter [data-type]').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    setAuditUserType(btn.dataset.type as 'all' | 'staff' | 'drivers');
    setAuditPage(0);
    laRecalculate();
  });
}

export function resetLoginAuditPage(): void {
  auditRawRows     = [];
  auditSearch      = '';
  auditPage        = 0;
  auditUserType    = 'all';
  auditInitialized = false;

  document.getElementById('laEmpty')?.classList.remove('hidden');
  document.getElementById('laResults')?.classList.add('hidden');
  document.getElementById('laErrBox')?.classList.add('hidden');

  const exportBtn = document.getElementById('laExportBtn') as HTMLElement | null;
  if (exportBtn) exportBtn.style.display = 'none';

  const runBtn = document.getElementById('laRunBtn') as HTMLButtonElement & { _wired?: boolean } | null;
  if (runBtn) runBtn._wired = false;

  const searchInput = document.getElementById('laSearch') as HTMLInputElement | null;
  if (searchInput) searchInput.value = '';
}

// ── Date range ────────────────────────────────────────────────────────────

function laSetRange(range: string): void {
  const now     = new Date();
  const toLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let fromLocal = new Date(toLocal);

  if (range === '7d')  fromLocal.setDate(fromLocal.getDate() - 6);
  if (range === '30d') fromLocal.setDate(fromLocal.getDate() - 29);
  if (range === 'mtd') fromLocal.setDate(1);
  if (range === 'ytd') fromLocal = new Date(toLocal.getFullYear(), 0, 1);

  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  (document.getElementById('laFrom') as HTMLInputElement).value = fmt(fromLocal);
  (document.getElementById('laTo')   as HTMLInputElement).value = fmt(toLocal);
}

// ── Recalculate (re-render on sort/search/page change, no re-fetch) ───────

export function laRecalculate(): void {
  if (!auditRawRows.length) return;
  renderLoginAuditResults(auditRawRows, auditSortCol, auditSortAsc);
}
